import { ReactNode, useState } from 'react';
import { useRouteMatch, NavLink, Link, useHistory } from 'react-router-dom';
import cx from 'classnames';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { WrapIf } from '../WrapIf';

const SIDEBAR_ICON_SIZE = 18;
const SIDEBAR_ICON_STROKE_WIDTH = 1.75;

// Every sidebar icon goes through here so size and stroke stay consistent.
// The 32px slot keeps labels on the same column with or without an icon.
export const SidebarIcon = ({
    icon: Icon,
    className,
}: {
    icon: LucideIcon;
    className?: string;
}) => (
    <span
        className={cx(
            'flex items-center justify-center w-8 h-8 shrink-0',
            className,
        )}
        aria-hidden="true"
    >
        <Icon
            size={SIDEBAR_ICON_SIZE}
            strokeWidth={SIDEBAR_ICON_STROKE_WIDTH}
        />
    </span>
);

// Shared look for sidebar rows.
export const sidebarItemClassName = (isActive: boolean) =>
    cx(
        'flex justify-start items-center px-3 py-1.5 w-full rounded-lg text-plum transition-colors duration-200',
        isActive
            ? 'cursor-default bg-starkWhite font-medium'
            : 'hover:cursor-pointer hover:bg-starkWhite hover:bg-opacity-50',
    );

export const SidebarDivider = () => (
    <div className="w-full px-4 my-2" aria-hidden="true">
        <div className="border-t border-starkWhite" />
    </div>
);

const CustomNavLink = ({
    label,
    to,
    leadingIcon,
    isLight,
    exact,
    onClick,
}: {
    label: string;
    to?: string;
    leadingIcon: LucideIcon;
    isLight?: boolean;
    exact?: boolean;
    onClick?: () => void;
}) => {
    const isActive = Boolean(useRouteMatch(to ? { path: to, exact } : {}));

    return (
        <>
            {to ? (
                <NavLink
                    to={to}
                    className={cx(
                        !isLight && sidebarItemClassName(isActive),
                        isLight &&
                            'flex justify-start items-center px-4 rounded py-2 w-full bg-none border-l-[3px] duration-200 transition-all hover:cursor-pointer',
                        isActive &&
                            isLight &&
                            'cursor-default border-transparent hover:bg-woodyBrown woodyBrown hover:bg-opacity-5 text-woodyBrown bg-white',
                        !isActive &&
                            isLight &&
                            'border-transparent hover:bg-peachOrange hover:bg-opacity-20 text-woodyBrown',
                    )}
                    onClick={onClick}
                >
                    <SidebarIcon icon={leadingIcon} />
                    <p
                        className={cx(
                            'tracking-tight align-middle text-left whitespace-nowrap',
                        )}
                    >
                        {label}
                    </p>
                </NavLink>
            ) : (
                <button
                    className={cx(
                        !isLight && sidebarItemClassName(false),
                        isLight &&
                            'flex justify-start items-center px-4 rounded py-2 w-full bg-none border-l-[3px] border-transparent duration-200 transition-all hover:cursor-pointer',
                        isActive &&
                            isLight &&
                            'cursor-default hover:bg-woodyBrown woodyBrown hover:bg-opacity-5 text-woodyBrown  ',
                        !isActive &&
                            isLight &&
                            ' hover:bg-peachOrange hover:bg-opacity-20 text-woodyBrown',
                    )}
                    onClick={onClick}
                >
                    <SidebarIcon icon={leadingIcon} />
                    <p
                        className={cx(
                            'tracking-tight align-middle text-left whitespace-nowrap',
                        )}
                    >
                        {label}
                    </p>
                </button>
            )}
        </>
    );
};

const NavDropdown = ({
    to,
    children,
    label,
    leadingIcon,
    indentChildren = true,
    isLight = false,
    disabled = false,
    shouldLink,
}: {
    to?: string;
    children?: ReactNode | ReactNode[];
    label: string;
    leadingIcon: LucideIcon;
    isActive?: boolean;
    disabled?: boolean;
    indentChildren?: boolean;
    isLight?: boolean;
    shouldLink?: boolean;
}) => {
    const { push } = useHistory();
    const match = useRouteMatch(to ? to : {});
    const isActive = shouldLink ? Boolean(match?.isExact) : Boolean(match);
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <button
                onClick={() =>
                    shouldLink ? push(to || '/') : setExpanded(!expanded)
                }
                disabled={disabled || isActive}
                className={cx(
                    'group',
                    !isLight && sidebarItemClassName(isActive),
                    isLight &&
                        'flex justify-start items-center px-4 rounded py-2 w-full border-l-[3px] transition duration-200',
                    isActive &&
                        isLight &&
                        'cursor-default border-transparent hover:bg-woodyBrown  hover:bg-opacity-5 text-woodyBrown',
                    !isActive &&
                        isLight &&
                        'border-transparent hover:bg-peachOrange hover:bg-opacity-20 text-woodyBrown',
                )}
            >
                <SidebarIcon icon={leadingIcon} />
                <p className="tracking-tight font-body align-middle text-left flex-1 whitespace-nowrap group-disabled:pointer-events-none">
                    {label}
                </p>

                <SidebarIcon
                    icon={ChevronDown}
                    className={cx(
                        'transition-transform duration-200',
                        expanded || isActive ? 'rotate-180' : 'rotate-0',
                        isActive && 'group-disabled:pointer-events-none',
                    )}
                />
            </button>

            <ul
                className={cx(
                    'flex flex-col justify-start w-full overflow-y-hidden items-end transition-all duration-300 z-10',
                    expanded || isActive
                        ? 'visible opacity-100 max-h-max'
                        : 'invisible  opacity-0 max-h-0',

                    indentChildren && 'ml-[48px]',
                )}
            >
                {children}
            </ul>
        </>
    );
};

type Item = {
    label: string;
    nestedItems?: { screen: Screen; label: string }[];
    screen?: string;
    icon: LucideIcon;
    children?: ReactNode | ReactNode[];
    isNested?: boolean;
    isActive?: boolean;
    disabled?: boolean;
    indentChildren?: boolean;
    isLight?: boolean;
    // Only highlight on this exact route, e.g. a home route that prefixes others
    exact?: boolean;
    onClick?: () => void;
    shouldLink?: boolean;
};

export const NavItem = ({
    label,
    children,
    screen,
    icon,
    indentChildren = true,
    isActive = false,
    disabled = false,
    isLight = false,
    exact,
    onClick,
    shouldLink,
}: Item) => {
    return (
        <>
            {children ? (
                <NavDropdown
                    to={screen}
                    label={label}
                    leadingIcon={icon}
                    indentChildren={indentChildren}
                    isActive={isActive}
                    disabled={disabled}
                    shouldLink={shouldLink}
                >
                    {children}
                </NavDropdown>
            ) : (
                <CustomNavLink
                    to={screen}
                    isLight={isLight}
                    leadingIcon={icon}
                    label={label}
                    exact={exact}
                    onClick={onClick}
                />
            )}
        </>
    );
};

export const NavButton = ({
    to,
    label,
    leadingIcon,
    isActive = false,
    onClick,
    trailingIcon,
    isLight = false,
}: {
    label: string;
    isActive?: boolean;
    leadingIcon?: LucideIcon;
    trailingIcon?: LucideIcon;
    isLight?: boolean;
} & XOR<{ to: string; onClick?: () => void }, { onClick?: () => void }>) => {
    const match = useRouteMatch(to ? to : {});
    return (
        <WrapIf
            condition={to !== undefined}
            wrapper={children => (
                <Link
                    className={cx(
                        'w-full',
                        isActive
                            ? 'bg-starkWhite text-plum rounded-lg pointer-events-none'
                            : 'text-plum',
                    )}
                    to={to || '/'}
                    onClick={onClick}
                >
                    {children}
                </Link>
            )}
        >
            <button
                onClick={onClick}
                disabled={!!match}
                className={cx(
                    'flex justify-start items-center px-4  min-w-full w-full bg-none rounded-lg',
                    // match && 'stroke-[2.5px] font-bold opacity-100',
                    // !match &&
                    'font-normal tracking-wide hover:tracking-tighter transition-all',
                    isLight
                        ? 'hover:bg-woodyBrown hover:bg-opacity-5  py-2'
                        : 'hover:bg-starkWhite hover:bg-opacity-50 py-1.5',
                )}
            >
                {leadingIcon && <SidebarIcon icon={leadingIcon} />}
                <p className="tracking-tight  align-middle text-left flex-1  whitespace-nowrap">
                    {label}
                </p>
                {trailingIcon && <SidebarIcon icon={trailingIcon} />}
            </button>
        </WrapIf>
    );
};
